import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Sparkles } from 'lucide-react';
import { useApp } from '@/store/app';
import { suggestToolForFile } from '@/lib/intent';
import { toast } from 'sonner';

/** Fullscreen "drop a file anywhere" overlay. Routes the file to a relevant tool. */
export function GlobalDropOverlay() {
  const navigate = useNavigate();
  const { setPendingFile } = useApp();
  const [over, setOver] = useState(false);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    function onEnter(e: DragEvent) {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      setCounter((n) => n + 1);
      setOver(true);
    }
    function onLeave(e: DragEvent) {
      e.preventDefault();
      setCounter((n) => {
        const next = n - 1;
        if (next <= 0) setOver(false);
        return Math.max(0, next);
      });
    }
    function onOver(e: DragEvent) {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
    }
    function onDrop(e: DragEvent) {
      if (!e.dataTransfer?.files.length) return;
      e.preventDefault();
      setOver(false);
      setCounter(0);
      const file = e.dataTransfer.files[0];
      const target = suggestToolForFile(file);
      if (!target) {
        toast.error('No matching tool for this file type');
        return;
      }
      setPendingFile(file);
      navigate(`/tool/${target}`);
      toast.success(`Routed "${file.name}"`, { description: 'Opening the right tool…' });
    }

    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [navigate, setPendingFile]);

  return (
    <AnimatePresence>
      {over && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center"
        >
          <div className="absolute inset-0 bg-bg/85 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.94 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.94 }}
            className="relative flex flex-col items-center text-center"
          >
            <div className="size-28 rounded-3xl bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 flex items-center justify-center border-[3px] border-fg/85 dark:border-fg/40 shadow-sticker-lg mb-5 animate-bob rotate-[-4deg]">
              <Upload size={40} className="text-white drop-shadow-[0_2px_0_rgba(0,0,0,0.3)]" />
            </div>
            <div className="text-2xl font-semibold tracking-tight">Drop to start</div>
            <div className="text-sm text-fg-muted mt-2 flex items-center gap-1.5">
              <Sparkles size={12} className="text-orange-500 dark:text-amber-400" />
              The agent will pick the best utility
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
